import { Skeleton } from '@mui/material';
import { ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';
import { useMemo, useState } from 'react';
import { TvDetailsHeader } from '#components/Details/DetailsHeader/TvDetailsHeader.tsx';
import { PlayerSelectorTabs } from '#components/PlayerSelectorTabs/index.tsx';
import { Section, SectionColor } from '#components/Section/index.tsx';
import { type TileItem } from '#components/Tile/types.ts';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { ImageSize, PosterSize } from '#enums/imageSizes.ts';
import { ScreenFormatType, SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { get } from '#helpers/otherHelpers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { SectionHeading } from '#styled.ts';
import { type ActiveVideo } from '#types.ts';
import { BackdropsCarousel } from '../../BackdropsCarousel/index.tsx';
import { CastAndCrewCarousel } from '../../CastAndCrew/Carousel/index.tsx';
import { Query as CastAndCrewHighlights } from '../../CastAndCrew/Highlights/index.ts';
import { PlayerSelectorModal } from '../../Modal/PlayerSelectorModal.tsx';
import { Overview } from '../../Overview/index.tsx';
import { PosterImage } from '../../PosterImage/index.tsx';
import { RecommendationsCarousel } from '../../RecommendationsCarousel/index.tsx';
import { SeasonsCarousel } from '../../SeasonsCarousel/index.tsx';
import { SimilarCarousel } from '../../SimilarCarousel/index.tsx';
import { JustWatchLogo } from '../../WatchProviders/JustWatchLogo.tsx';
import { WatchProvidersQuery } from '../../WatchProviders/index.ts';
import {
  CastAndCrewHighlightsContainer,
  DetailsBody,
  PosterImageContainer,
  PosterImageOverviewContainer,
  PoweredBy,
  WatchProvidersHeadingContainer,
} from '../styled.ts';

export type TvDetailsProps = {
  data: Tv;
  setActivePreview?: (item: Pick<TileItem, 'id' | 'type'>) => void;
};

export const TvDetails = ({ data, setActivePreview }: TvDetailsProps) => {
  const { id, name, overview, posterPath, status } = data;
  const { config, uaParser } = useToolbelt();
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);

  const memoTvDetails = useMemo(
    () => (
      <>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <TvDetailsHeader
            data={data}
            linkTitle={isAncestorSlideoutDrawer}
            setActivePreview={setActivePreview}
            showExpandIcon={isAncestorSlideoutDrawer}
          />
        </Section>
        <Section color={SectionColor.Black}>
          <PlayerSelectorTabs id={id} title={name} type={ScreenFormatType.Tv} />
        </Section>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <DetailsBody>
            <PosterImageContainer>
              <PosterImage
                id={id}
                src={buildImagePath(posterPath, ImageSize.Poster, PosterSize.MD, config.images)}
                status={status}
                title={name}
                type={SectionType.Tv}
              />
            </PosterImageContainer>
            <PosterImageOverviewContainer>
              {overview ? <Overview content={overview} id={id} name={name} /> : undefined}
            </PosterImageOverviewContainer>
            <CastAndCrewHighlightsContainer hasOverview={!!overview}>
              <CastAndCrewHighlights id={id} type={ScreenFormatType.Tv} />
            </CastAndCrewHighlightsContainer>
          </DetailsBody>
        </Section>
        {!isAncestorSlideoutDrawer && (
          <>
            <WatchProvidersQuery id={id} type={ScreenType.Tv}>
              {watchProviders =>
                watchProviders ? (
                  <Section
                    color={SectionColor.Grey}
                    threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}
                    title={
                      <WatchProvidersHeadingContainer>
                        {get<number>(watchProviders, 'props.providers.length') > 0 ? (
                          <>
                            <SectionHeading variant="h6">Where to watch</SectionHeading>
                            <div>
                              <PoweredBy>Powered by</PoweredBy>
                              <JustWatchLogo />
                            </div>
                          </>
                        ) : (
                          <Skeleton sx={{ fontSize: '1.25rem' }} variant="text" width={120} />
                        )}
                      </WatchProvidersHeadingContainer>
                    }
                  >
                    {watchProviders}
                  </Section>
                ) : undefined
              }
            </WatchProvidersQuery>
            <SeasonsCarousel id={id} setActivePreview={setActivePreview}>
              {seasonsCarousel => (seasonsCarousel ? <Section title="Seasons">{seasonsCarousel}</Section> : undefined)}
            </SeasonsCarousel>
            <CastAndCrewCarousel id={id} setActivePreview={setActivePreview} subType="cast" type={ScreenFormatType.Tv}>
              {castCarousel => (castCarousel ? <Section title="Cast">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <CastAndCrewCarousel id={id} setActivePreview={setActivePreview} subType="crew" type={ScreenFormatType.Tv}>
              {castCarousel => (castCarousel ? <Section title="Crew">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <RecommendationsCarousel id={id} setActivePreview={setActivePreview} type={ScreenType.Tv}>
              {recommendationsCarousel =>
                recommendationsCarousel ? <Section title="Recommended">{recommendationsCarousel}</Section> : undefined
              }
            </RecommendationsCarousel>
            <BackdropsCarousel id={id} title={name} type={ScreenType.Tv}>
              {backdropsCarousel => <Section title="Photos">{backdropsCarousel}</Section>}
            </BackdropsCarousel>
            <SimilarCarousel id={id} setActivePreview={setActivePreview} type={ScreenType.Tv}>
              {similarCarousel => (similarCarousel ? <Section title="Similar">{similarCarousel}</Section> : undefined)}
            </SimilarCarousel>
          </>
        )}
      </>
    ),
    [id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      {activeVideo?.src && activeVideo.src.length > 0 ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={ScreenType.Tv}
        />
      ) : undefined}
      {memoTvDetails}
    </div>
  );
};
